import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { getUserRole, getUserId } from "../utils/Auth";
import { useTheme } from "../context/ThemeContext";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    PieChart,
    Pie,
    Cell
} from "recharts";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";

// ------------------- MONTH LABELS -------------------
const monthNames = [
    "Ian", "Feb", "Mar", "Apr", "Mai", "Iun",
    "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"
];

// ------------------- BASE MONTH STRUCTURE -------------------
const baseMonths = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    totalSales: 0,
    totalOrders: 0,
}));

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);


// ------------------- EUR FORMAT ---------------------
const formatEUR = (value) =>
    new Intl.NumberFormat("ro-RO", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    }).format(value || 0);

// ------------------- CUSTOM TOOLTIP ---------------------
const CustomTooltip = ({ active, payload, label, darkMode }) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0].payload;
    const monthLabel = monthNames[label - 1];

    return (
        <div
            className={`p-4 rounded-xl shadow-xl ${darkMode
                ? "bg-gray-800 border border-gray-700 text-gray-100"
                : "bg-white border border-gray-200 text-gray-900"
                }`}
        >
            <div className="font-bold text-lg mb-1">{monthLabel}</div>

            <div className="text-sm mt-1">
                <span className="opacity-70">Vânzări NET:</span>
                <span className="font-semibold">{formatEUR(row.totalSales)}</span>
            </div>

            <div className="text-sm mt-1">
                <span className="opacity-70">Comenzi:</span>{" "}
                <span className="font-semibold">{row.totalOrders}</span>
            </div>
        </div>
    );
};

const DonutProfitChart = ({ percent, darkMode }) => {
    const data = [
        { name: "Profit", value: percent },
        { name: "Rest", value: 100 - percent }
    ];

    const COLORS = darkMode
        ? ["#16a34a", "#6b7280"]   // profit green + grey visible in dark mode
        : ["#22c55e", "#d1d5db"]; // brighter green + subtle grey in light mode

    return (
        <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        innerRadius={70}
                        outerRadius={100}
                        stroke="none"
                    >
                        {data.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index]} />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>

            <div
                className="absolute font-bold text-center"
                style={{
                    fontSize: "28px",
                    lineHeight: "1",
                    color: darkMode ? "white" : "black",
                }}
            >
                {percent.toFixed(1)}%
            </div>
        </div>
    );
};

const AgentSalesTooltip = ({ active, payload, label, darkMode }) => {
    if (!active || !payload || !payload.length) return null;

    const p = payload[0];

    return (
        <div
            style={{
                backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                border: `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}`,
                borderRadius: "10px",
                padding: "12px",
                color: darkMode ? "#f9fafb" : "#111827",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}
        >
            <div className="font-semibold mb-1">Agent: {label}</div>

            <div className="text-sm">
                <span className="opacity-70">Vânzări NET:</span>{" "}
                <span className="font-bold">{formatEUR(p.value)}</span>
            </div>
        </div>
    );
};

const DonutCommissionChart = ({ percent, darkMode }) => {
    const data = [
        { name: "Comision", value: percent },
        { name: "Rest", value: 100 - percent }
    ];

    const COLORS = darkMode
        ? ["#f43f5e", "#6b7280"]   // jól kontrasztál a card bg-vel
        : ["#e11d48", "#c9cbceff"];

    return (
        <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        innerRadius={70}
                        outerRadius={100}
                        stroke="none"
                    >
                        {data.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index]} />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>

            {/* Közepe – fixen középre pozicionálva */}
            <div
                className="absolute font-bold text-center"
                style={{
                    fontSize: "28px",
                    lineHeight: "1",
                    color: darkMode ? "white" : "black",
                }}
            >
                {percent.toFixed(1)}%
            </div>
        </div>
    );
};

const SalesStatistics = () => {
    const { darkMode } = useTheme();
    const role = getUserRole();
    const loggedUserId = getUserId();

    const [tab, setTab] = useState(role === "Admin" ? "company" : "me");
    const [year, setYear] = useState(new Date().getFullYear());

    const [usersYearly, setUsersYearly] = useState([]);
    const [userMonthly, setUserMonthly] = useState([]);
    const [usersList, setUsersList] = useState([{ id: loggedUserId, name: "Loading..." }]);

    const [companyMonthly, setCompanyMonthly] = useState([]);
    const [companyTotals, setCompanyTotals] = useState({
        totalSales: 0,
        totalOrders: 0
    });

    const [selectedCompany, setSelectedCompany] = useState("all");
    // all | marmorex | granit

    const [selectedKpiCompany, setSelectedKpiCompany] = useState("all");
    // all | marmorex | granit  — used only in KPI tab

    const [selectedUserId, setSelectedUserId] = useState(role === "Admin" ? "" : loggedUserId);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [monthlyStats, setMonthlyStats] = useState(null);
    const [salesPerAgent, setSalesPerAgent] = useState([]);

    const [sourceStats, setSourceStats] = useState([]);
    const [sourceMonthly, setSourceMonthly] = useState([]);


    const token = localStorage.getItem("token");

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const companyLabel =
        selectedCompany === "all"
            ? "Toate firmele"
            : selectedCompany === "marmorex"
                ? "Marmorex"
                : "G.O.";

    const trimFutureMonths = (monthsArray) => {
        if (year !== currentYear) return monthsArray;
        return monthsArray.filter((m) => m.month <= currentMonth);
    };

    const fetchUsersList = async () => {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;

        const data = await res.json();

        const normalized = data.map((u) => ({
            id: u.id,
            name:
                u.fullName ||
                u.name ||
                u.userName ||
                u.username ||
                u.email ||
                "Utilizator",
        }));

        setUsersList(normalized);
    };

    const SOURCE_COLORS = {
        Neclasificat: "#9ca3af",
        Online: "#3b82f6",
        Recomandare: "#a855f7",
        Recurent: "#22c55e",
    };

    const fetchSourceStats = async () => {
        const res = await fetch(
            `${API_BASE_URL}/api/stats/source/years?year=${year}&company=${selectedCompany}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) setSourceStats(await res.json());
    };

    const fetchSourceMonthly = async () => {
        const res = await fetch(
            `${API_BASE_URL}/api/stats/source/months?year=${year}&company=${selectedCompany}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const raw = await res.json();
        // pivot: [{month, Online, Recomandare, Recurent}]
        const pivoted = Array.from({ length: 12 }, (_, i) => ({
            month: i + 1,
            Neclasificat: 0,
            Online: 0,
            Recomandare: 0,
            Recurent: 0,
        }));
        raw.forEach(({ month, source, totalSales }) => {
            const row = pivoted.find(r => r.month === month);
            if (row && source) row[source] = totalSales;
        });
        setSourceMonthly(trimFutureMonths(pivoted));
    };

    const fetchCompanyMonthly = async () => {        const res = await fetch(
            `${API_BASE_URL}/api/stats/company/months?year=${year}&company=${selectedCompany}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) return;

        const data = await res.json();

        const merged = trimFutureMonths(
            baseMonths.map(m => data.find(x => x.month === m.month) || m)
        );

        setCompanyMonthly(merged);

        setCompanyTotals({
            totalSales: merged.reduce((s, m) => s + m.totalSales, 0),
            totalOrders: merged.reduce((s, m) => s + m.totalOrders, 0)
        });
    };

    const fetchUsersYearly = async () => {
        const res = await fetch(
            `${API_BASE_URL}/api/stats/users/years?year=${year}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) setUsersYearly(await res.json());
    };

    const fetchUserMonthly = async () => {
        const userIdToLoad =
            role === "Admin" && tab === "users" ? selectedUserId : loggedUserId;

        const res = await fetch(
            `${API_BASE_URL}/api/stats/users/${userIdToLoad}/months?year=${year}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;

        const data = await res.json();

        const merged = baseMonths.map((m) => {
            const found = data.find((x) => x.month === m.month);
            return found ? found : m;
        });

        setUserMonthly(trimFutureMonths(merged));
    };

    useEffect(() => {
        if (role === "Admin") {
            fetchUsersList();
        }
    }, []);

    useEffect(() => {
        if (role !== "Admin") return;
        if (tab !== "company") return;

        fetchCompanyMonthly();
        fetchUsersYearly();

    }, [year, selectedCompany, tab]);

    useEffect(() => {
        if (role !== "Admin") return;
        if (tab !== "surse") return;

        fetchSourceStats();
        fetchSourceMonthly();

    }, [year, selectedCompany, tab]);

    useEffect(() => {
        if (role !== "Admin") return;
        if (tab !== "users") return;

        fetchUsersYearly();
    }, [year, tab]);

    useEffect(() => {
        if (tab !== "users" && tab !== "me") return;
        if (role === "Admin" && tab === "users" && !selectedUserId) return;

        fetchUserMonthly();

    }, [year, tab, selectedUserId]);

    useEffect(() => {
        const loadMonthlyStats = async () => {
            try {
                const token = localStorage.getItem("token");

                const targetUserId = role === "Admin" ? selectedUserId : loggedUserId;

                const res = await fetch(
                    `${API_BASE_URL}/api/dashboard/monthly-stats?month=${selectedMonth}&userId=${targetUserId}&company=${selectedKpiCompany}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!res.ok) throw new Error("Eroare la monthly stats");

                const data = await res.json();
                setMonthlyStats(data);
                const res2 = await fetch(
                    `${API_BASE_URL}/api/dashboard/sales-per-agent?month=${selectedMonth}&company=${selectedKpiCompany}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res2.ok) setSalesPerAgent(await res2.json());
            } catch (err) {
                console.error("Monthly stats error:", err);
            }
        };

        loadMonthlyStats();
    }, [selectedMonth, selectedUserId, selectedKpiCompany]);

    const cardClass = darkMode
        ? "bg-gray-800 text-white border border-gray-700"
        : "bg-white text-black border border-gray-200";

    const innerCardClass = darkMode
        ? "bg-gray-700 text-gray-100 border border-gray-600"
        : "bg-white text-gray-900 border border-gray-300";

    const tabActive =
        "px-5 py-2 rounded-full bg-red-600 text-white shadow-[0_0_0_2px_rgba(220,38,38,0.55)] font-semibold";

    const tabInactive = `px-5 py-2 rounded-full font-medium border ${darkMode
        ? "bg-gray-800 border-gray-700 text-gray-200"
        : "bg-white border-gray-200 text-gray-800"
        } hover:bg-red-50 hover:text-red-700 transition`;

    const activeMonthly = companyMonthly;

    const activeTotalSales = companyTotals.totalSales;
    const activeTotalOrders = companyTotals.totalOrders;

    const activeColor =
        selectedCompany === "marmorex"
            ? "#dc2626"
            : selectedCompany === "granit"
                ? "#2563eb"
                : "#16a34a"; // ALL


    // -------------------------- RETURN --------------------------
    return (
        <div className="w-full min-h-screen">
            <h1 className="text-3xl font-bold mb-8 tracking-wide">Statistici de vânzări</h1>

            {/* ---------------- Tabs ---------------- */}
            {role === "Admin" && (
                <div className="flex flex-wrap gap-4 mb-8">
                    <button
                        className={tab === "company" ? tabActive : tabInactive}
                        onClick={() => setTab("company")}
                    >
                        Firmă
                    </button>

                    <button
                        className={tab === "users" ? tabActive : tabInactive}
                        onClick={() => {
                            setTab("users");
                            if (!selectedUserId && usersList.length > 0) {
                                setSelectedUserId(usersList[0].id);
                            }
                        }}
                    >
                        Agenți
                    </button>

                    <button
                        className={tab === "me" ? tabActive : tabInactive}
                        onClick={() => {
                            setSelectedUserId(loggedUserId);
                            setTab("me");
                        }}
                    >
                        Eu
                    </button>
                    <button
                        className={tab === "kpi" ? tabActive : tabInactive}
                        onClick={() => {
                            if (role === "Admin") setSelectedUserId("");
                            setTab("kpi");
                        }}
                    >
                        KPI
                    </button>

                    <button
                        className={tab === "surse" ? tabActive : tabInactive}
                        onClick={() => setTab("surse")}
                    >
                        Surse
                    </button>

                </div>
            )}

            {/* ---------------- Year + User Filter ---------------- */}
            {tab !== "kpi" && (
                <div className="flex flex-wrap items-center gap-4 mb-10 justify-end w-full">
                    {(tab === "company" || tab === "surse") && role === "Admin" && (
                        <div className="flex items-center gap-3">
                            <label className="font-semibold">Companie:</label>
                            <select
                                className={`px-3 py-2 rounded-lg border-0 font-medium shadow-sm ${darkMode
                                    ? "bg-gray-700 text-gray-200"
                                    : "bg-gray-100 text-gray-800"
                                    }`}
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                <option value="all">Toate firmele</option>
                                <option value="marmorex">Marmorex</option>
                                <option value="granit">G.O.</option>
                            </select>
                        </div>
                    )}


                    <div className="flex items-center gap-3">
                        <label className="font-semibold">Anul:</label>
                        <select
                            className={`px-3 py-2 rounded-lg border-0 font-medium shadow-sm ${darkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-800"
                                }`}
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {[2023, 2024, 2025, 2026].map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>

                    {role === "Admin" && tab === "users" && (
                        <div className="flex items-center gap-3">
                            <label className="font-semibold">Utilizator:</label>
                            <select
                                className={`px-3 py-2 rounded-lg border-0 font-medium shadow-sm ${darkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-800"
                                    }`}
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                            >
                                {usersList.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* ---------------- COMPANY VIEW ---------------- */}
            {tab === "company" && role === "Admin" && (
                <div className={`p-6 rounded-2xl shadow-lg mb-10 ${cardClass}`}>


                    {/* ---- Annual Summary Cards ---- */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

                        <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                            <h3 className="text-sm opacity-70">
                                {companyLabel} – Vânzări ({year})
                            </h3>

                            <p
                                className="text-2xl font-bold mt-2"
                                style={{ color: activeColor }}
                            >
                                {formatEUR(activeTotalSales)}
                            </p>
                        </div>

                        <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                            <h3 className="text-sm opacity-70">
                                {companyLabel} – Comenzi ({year})
                            </h3>

                            <p
                                className="text-2xl font-bold mt-2"
                                style={{ color: activeColor }}
                            >
                                {activeTotalOrders}
                            </p>
                        </div>

                    </div>

                    <h3 className="text-xl font-semibold mb-4">
                        {companyLabel} – Vânzări lunare ({year})
                    </h3>

                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={activeMonthly}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                            <YAxis />
                            <Tooltip content={(p) => <CustomTooltip {...p} darkMode={darkMode} />} />
                            <Bar
                                dataKey="totalSales"
                                fill={activeColor}
                                radius={[8, 8, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>

                </div>
            )}

            {/* ---------------- USERS VIEW ---------------- */}
            {tab === "users" && role === "Admin" && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                    <div className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                        <h3 className="text-xl font-semibold mb-4">Top Agenți ({year})</h3>

                        <table className="w-full text-sm">
                            <thead>
                                <tr
                                    className={
                                        darkMode ? "border-b border-gray-600" : "border-b border-gray-200"
                                    }
                                >
                                    <th className="py-2 text-left">Nume</th>
                                    <th className="py-2 text-left">Vânzări totale</th>
                                    <th className="py-2 text-left">Comenzi</th>
                                </tr>
                            </thead>

                            <tbody>
                                {usersYearly.map((u, idx) => (
                                    <tr
                                        key={u.userId}
                                        className={`border-b ${darkMode ? "border-gray-700" : "border-gray-100"
                                            } ${idx % 2 === 0 ? (darkMode ? "bg-gray-800/60" : "bg-gray-50") : ""}`}
                                    >
                                        <td className="py-2">{u.userName}</td>
                                        <td className="py-2">{formatEUR(u.totalSales)}</td>
                                        <td className="py-2">{u.totalOrders}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                        <h3 className="text-xl font-semibold mb-4">Vânzări totale pe lună</h3>

                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={userMonthly}>
                                <CartesianGrid
                                    stroke={darkMode ? "#4b5563" : "#8a8b8dff"}
                                    strokeDasharray="3 3"
                                    strokeOpacity={0.6}
                                />

                                <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                                <YAxis />

                                <Tooltip
                                    content={(props) => <CustomTooltip {...props} darkMode={darkMode} key={props.label} />}
                                    isAnimationActive={false}
                                    wrapperStyle={{
                                        transition: "transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1.0)",
                                    }}

                                />

                                <Line
                                    type="monotone"
                                    dataKey="totalSales"
                                    stroke="#e11d48"
                                    strokeWidth={3}
                                    dot={{
                                        r: 4,
                                        strokeWidth: 2,
                                        stroke: "#e11d48",
                                        fill: darkMode ? "#1f2937" : "#ffffff",
                                    }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* ---------------- ME VIEW ---------------- */}
            {tab === "me" && (
                <div className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                    <h3 className="text-xl font-semibold mb-4">Vânzări totale pe lună ({year})</h3>

                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={userMonthly}>
                            <CartesianGrid
                                stroke={darkMode ? "#4b5563" : "#8a8b8dff"}
                                strokeDasharray="3 3"
                                strokeOpacity={0.6}
                            />

                            <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                            <YAxis />

                            <Tooltip
                                content={(props) => <CustomTooltip {...props} darkMode={darkMode} key={props.label} />}
                                isAnimationActive={false}
                                wrapperStyle={{
                                    transition: "transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1.0)",
                                }}

                            />


                            <Line
                                type="monotone"
                                dataKey="totalSales"
                                stroke="#e11d48"
                                strokeWidth={3}
                                dot={{
                                    r: 4,
                                    strokeWidth: 2,
                                    stroke: "#e11d48",
                                    fill: darkMode ? "#1f2937" : "#ffffff",
                                }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ---------------- SURSE VIEW ---------------- */}
            {tab === "surse" && role === "Admin" && (
                <div className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                    <h3 className="text-xl font-semibold mb-6">
                        Sursa clienților – {companyLabel} ({year})
                    </h3>

                    {/* Yearly summary cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {["Neclasificat", "Online", "Recomandare", "Recurent"].map((src) => {
                            const entry = sourceStats.find(s => s.source === src) || { totalOrders: 0, totalSales: 0 };
                            const color = SOURCE_COLORS[src];
                            return (
                                <div key={src} className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                                    <h4 className="text-sm opacity-70 mb-1">{src}</h4>
                                    <p className="text-2xl font-bold" style={{ color }}>{formatEUR(entry.totalSales)}</p>
                                    <p className="text-sm opacity-60 mt-1">{entry.totalOrders} comenzi</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pie chart */}
                    {sourceStats.length > 0 && (
                        <div className="flex flex-col items-center mb-10">
                            <h4 className="text-lg font-semibold mb-4">Distribuție vânzări ({year})</h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={sourceStats.map(s => ({ name: s.source, value: s.totalSales }))}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={110}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                                    >
                                        {sourceStats.map((s, i) => (
                                            <Cell key={i} fill={SOURCE_COLORS[s.source] || "#8884d8"} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => formatEUR(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Monthly stacked bar chart */}
                    <h4 className="text-lg font-semibold mb-4">Evoluție lunară pe sursă ({year})</h4>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={sourceMonthly}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tickFormatter={(m) => monthNames[m - 1]} />
                            <YAxis />
                            <Tooltip
                                formatter={(value, name) => [formatEUR(value), name]}
                                labelFormatter={(m) => monthNames[m - 1]}
                            />
                            <Bar dataKey="Neclasificat" stackId="src" fill={SOURCE_COLORS.Neclasificat} radius={[0,0,0,0]} />
                            <Bar dataKey="Online" stackId="src" fill={SOURCE_COLORS.Online} radius={[0,0,0,0]} />
                            <Bar dataKey="Recomandare" stackId="src" fill={SOURCE_COLORS.Recomandare} radius={[0,0,0,0]} />
                            <Bar dataKey="Recurent" stackId="src" fill={SOURCE_COLORS.Recurent} radius={[8,8,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ---------------- KPI VIEW ---------------- */}
            {tab === "kpi" && role === "Admin" && (                <div className={`p-6 rounded-2xl shadow-lg ${cardClass}`}>
                    <h3 className="text-2xl font-semibold mb-6">KPI – Performanță Lunara</h3>

                    {/* USER SELECTOR + DATE IN ONE LINE */}
                    <div className="mb-6 flex flex-wrap items-center gap-6">

                        {/* USER SELECTOR */}
                        <div className="flex items-center gap-3">
                            <label className="font-semibold">Utilizator:</label>

                            <select
                                className={`px-3 py-2 rounded-lg border ${darkMode
                                    ? "bg-gray-800 text-gray-200 border-gray-700"
                                    : "bg-white text-gray-800 border-gray-300"
                                    }`}
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                            >
                                <option value="">Toată firma</option>
                                {usersList.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* COMPANY FILTER FOR KPI */}
                        <div className="flex items-center gap-3">
                            <label className="font-semibold">Companie:</label>

                            <select
                                className={`px-3 py-2 rounded-lg border ${darkMode
                                    ? "bg-gray-800 text-gray-200 border-gray-700"
                                    : "bg-white text-gray-800 border-gray-300"
                                    }`}
                                value={selectedKpiCompany}
                                onChange={(e) => setSelectedKpiCompany(e.target.value)}
                            >
                                <option value="all">Toate firmele</option>
                                <option value="marmorex">Marmorex</option>
                                <option value="granit">Granit Online</option>
                            </select>
                        </div>

                        {/* DATE PICKER */}
                        <div className="flex items-center gap-3">
                            <label className="font-semibold">Luna:</label>

                            <DatePicker
                                selected={
                                    selectedMonth
                                        ? new Date(selectedMonth + "-01T00:00:00")
                                        : null
                                }
                                onChange={(d) => {
                                    if (!d) return;
                                    const m = `${d.getFullYear()}-${String(
                                        d.getMonth() + 1
                                    ).padStart(2, "0")}`;
                                    setSelectedMonth(m);
                                }}
                                dateFormat="MM/yyyy"
                                showMonthYearPicker
                                calendarStartDay={1}
                                portalId="datepicker-root"
                                customInput={
                                    <div
                                        className={`flex items-center justify-between w-[180px] h-[44px] px-4 
                    rounded-xl shadow-sm border-2 transition-all cursor-pointer
                    ${darkMode
                                                ? "bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700"
                                                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                                            }`}
                                    >
                                        <span className="font-semibold text-sm">
                                            {selectedMonth
                                                ? capitalize(
                                                    new Date(
                                                        selectedMonth + "-01T00:00:00"
                                                    ).toLocaleDateString("ro-RO", {
                                                        year: "numeric",
                                                        month: "long",
                                                    })
                                                )
                                                : "Alege luna"}
                                        </span>

                                        <Calendar
                                            className={`w-5 h-5 ${darkMode ? "text-gray-200" : "text-gray-700"
                                                }`}
                                        />
                                    </div>
                                }
                            />
                        </div>
                    </div>

                    {/* GUARDED CONTENT */}
                    {!monthlyStats ? (

                        <div className="text-center py-10 text-lg opacity-70">
                            Se încarcă datele...
                        </div>

                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">

                                <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                                    <h3 className="text-lg font-semibold opacity-70">Vânzări totale NET (lună)</h3>
                                    <p className="text-3xl font-bold mt-2">
                                        {monthlyStats.totalNetSales.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} €
                                    </p>
                                </div>

                                <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                                    <h3 className="text-lg font-semibold opacity-70">Comenzi totale (lună)</h3>
                                    <p className="text-3xl font-bold mt-2">{monthlyStats.totalOrders}</p>
                                </div>

                                <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                                    <h3 className="text-lg font-semibold opacity-70">Comision total</h3>
                                    <p className="text-3xl font-bold mt-2">
                                        {monthlyStats.totalCommission.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} €
                                    </p>
                                </div>

                                <div className={`p-6 rounded-xl shadow-md ${innerCardClass}`}>
                                    <h3 className="text-lg font-semibold opacity-70">Profit brut</h3>
                                    <p className="text-3xl font-bold mt-2">
                                        {monthlyStats.totalProfit.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} €
                                    </p>
                                </div>
                            </div>

                            {/* ----- Donut Chart: Comision (%) ----- */}
                            <div
                                className={`mb-12 grid transition-all duration-300
        ${role === "Admin" && !selectedUserId && salesPerAgent.length > 0
                                        ? "grid-cols-1 lg:grid-cols-4 gap-8"
                                        : "grid-cols-2 gap-4 justify-center max-w-[900px] mx-auto"
                                    }
    `}
                            >

                                {/* WRAPPER ANIMÁCIÓVAL */}
                                <div
                                    className={`
    transform transition-all duration-500 ease-in-out
    ${role === "Admin" && !selectedUserId && salesPerAgent.length > 0
                                            ? "-translate-x-10"   // ha van firmachart → eltoljuk balra
                                            : "translate-x-0"     // ha nincs → vissza középre
                                        }
  `}
                                >
                                    {/* Procent Comision */}
                                    <div className={`${innerCardClass} p-4 rounded-xl shadow-md w-[280px] h-[360px] mx-auto`}>
                                        <h3 className="text-lg font-semibold mb-3 text-center">
                                            Procent Comision
                                        </h3>
                                        <div className="flex flex-col items-center justify-center h-full">
                                            <DonutCommissionChart
                                                percent={monthlyStats.commissionPercent}
                                                darkMode={darkMode}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* MÁSODIK DONUT */}
                                <div
                                    className={`
    transform transition-all duration-500 ease-in-out
    ${role === "Admin" && !selectedUserId && salesPerAgent.length > 0
                                            ? "-translate-x-6"
                                            : "translate-x-0"
                                        }
  `}
                                >
                                    <div className={`${innerCardClass} p-4 rounded-xl shadow-md w-[280px] h-[360px] mx-auto`}>
                                        <h3 className="text-lg font-semibold mb-3 text-center">
                                            Marjă Profit (%)
                                        </h3>
                                        <div className="flex flex-col items-center justify-center h-full">
                                            <DonutProfitChart
                                                percent={monthlyStats.avgProfitMargin}
                                                darkMode={darkMode}
                                            />
                                        </div>
                                    </div>
                                </div>


                                {role === "Admin" && !selectedUserId && salesPerAgent.length > 0 && (
                                    <div className={`${innerCardClass} rounded-2xl shadow-lg lg:col-span-2`}>
                                        <h3 className="text-xl font-semibold mb-6 text-center">
                                            Vânzări pe agent (NET)
                                        </h3>

                                        <ResponsiveContainer width="100%" height={260}>
                                            <BarChart
                                                data={salesPerAgent}
                                                barCategoryGap="30%"
                                            >
                                                {/* Finom grid, kevésbé tolakodó */}
                                                <CartesianGrid
                                                    stroke={darkMode ? "#374151" : "#e5e7eb"}
                                                    strokeDasharray="4 4"
                                                    opacity={0.4}
                                                />

                                                {/* Tengely címkék modern stílusban */}
                                                <XAxis
                                                    dataKey="userName"
                                                    tick={{ fontSize: 12, fill: darkMode ? "#d1d5db" : "#374151" }}
                                                    interval={0}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12, fill: darkMode ? "#d1d5db" : "#374151" }}
                                                />

                                                {/* Custom tooltip – sokkal szebb */}
                                                <Tooltip
                                                    content={<AgentSalesTooltip darkMode={darkMode} />}
                                                    cursor={{ fill: darkMode ? "#11182722" : "#00000011" }}
                                                />

                                                {/* Oszlopok – vastagabb, esztétikusabb */}
                                                <Bar
                                                    dataKey="netSales"
                                                    fill="#dc2626"
                                                    radius={[10, 10, 4, 4]}
                                                    maxBarSize={50}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default SalesStatistics;
